import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useAIChat } from './useAIChat';
import { AI_BASE_URL } from './aiConfig';

const BRAND       = '#2b5a7e';
const BRAND_DARK  = '#214561';
const BRAND_LIGHT = '#6996b5';

const INTENT_META = {
  guide:  { label: 'Guide',  icon: 'bx-book-open',    color: '#009dac', bg: 'rgba(0,157,172,0.1)'  },
  data:   { label: 'Data',   icon: 'bx-bar-chart-alt-2', color: '#0d9488', bg: 'rgba(13,148,136,0.1)' },
  report: { label: 'Report', icon: 'bx-file',          color: '#2563eb', bg: 'rgba(37,99,235,0.1)'  },
};

const SUGGESTIONS = [
  { text: 'How do I create an NCR?',               intent: 'guide',  icon: 'bx-clipboard'      },
  { text: 'How many inspections were done today?', intent: 'data',   icon: 'bx-bar-chart-alt-2' },
  { text: 'Show me all open NCRs this week',       intent: 'data',   icon: 'bx-search-alt'      },
  { text: 'Generate a Daily Inspection Summary',   intent: 'report', icon: 'bx-file-blank'      },
  { text: 'How do I conduct an inspection?',       intent: 'guide',  icon: 'bx-wrench'          },
];

const AIChatPanel = ({ open, onClose }) => {
  const {
    messages, sendMessage, isStreaming, stopStreaming,
    clearHistory, capabilities, loadCapabilities,
  } = useAIChat();

  const [input, setInput] = useState('');
  const bottomRef         = useRef(null);
  const textareaRef       = useRef(null);

  useEffect(() => { loadCapabilities(); }, [loadCapabilities]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (el) { el.style.height = 'auto'; el.style.height = Math.min(el.scrollHeight, 120) + 'px'; }
  }, [input]);

  const handleSend = () => {
    const text = input.trim();
    if (!text || isStreaming) return;
    setInput('');
    sendMessage(text);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const visibleSuggestions = SUGGESTIONS.filter((s) => {
    if (s.intent === 'data'   && !capabilities?.canQueryData)       return false;
    if (s.intent === 'report' && !capabilities?.canGenerateReports) return false;
    return true;
  });

  return (
    <>
      <style>{PANEL_CSS}</style>
      <div
        className="ai-panel"
        style={{ transform: open ? 'translateX(0)' : 'translateX(100%)', opacity: open ? 1 : 0 }}
      >
        {/* ── Header ── */}
        <div className="ai-header">
          <div className="ai-header-left">
            <div className="ai-avatar-wrap">
              <div className="ai-avatar">
                <i className="bx bxs-star" style={{ fontSize: 17, color: '#fff' }} />
              </div>
              <span className="ai-online-dot" />
            </div>
            <div>
              <div className="ai-header-title">AI Assistant</div>
              {capabilities && (
                <div className="ai-header-sub">{capabilities.role} · {capabilities.userName}</div>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            <button className="ai-icon-btn" title="Clear history" onClick={clearHistory}>
              <i className="bx bx-trash" style={{ fontSize: 16 }} />
            </button>
            <button className="ai-icon-btn" title="Close" onClick={onClose}>
              <i className="bx bx-x" style={{ fontSize: 18 }} />
            </button>
          </div>
        </div>

        {/* ── Messages ── */}
        <div className="ai-messages ai-scroll">
          {messages.length === 0
            ? <EmptyState suggestions={visibleSuggestions} onSuggestion={(t) => sendMessage(t)} />
            : messages.map((msg) => <MessageBubble key={msg.id} msg={msg} />)
          }
          <div ref={bottomRef} />
        </div>

        {/* ── Input ── */}
        <div className="ai-input-wrap">
          <div className="ai-input-box">
            <textarea
              ref={textareaRef}
              className="ai-textarea"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isStreaming}
              placeholder="Ask anything about the system…"
              rows={1}
            />
            <div className="ai-input-footer">
              <span className="ai-input-hint">Enter to send · Shift+Enter for newline</span>
              {isStreaming ? (
                <button className="ai-send-btn ai-send-stop" onClick={stopStreaming} title="Stop">
                  <i className="bx bx-stop" style={{ fontSize: 16 }} />
                </button>
              ) : (
                <button
                  className={`ai-send-btn${!input.trim() ? ' ai-send-disabled' : ''}`}
                  onClick={handleSend}
                  disabled={!input.trim()}
                  title="Send"
                >
                  <i className="bx bx-send" style={{ fontSize: 16 }} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

/* ── Sub-components ── */

const MessageBubble = ({ msg }) => {
  const isUser     = msg.role === 'user';
  const intentMeta = INTENT_META[msg.intent];

  return (
    <div className={`ai-msg-row ai-msg-enter${isUser ? ' ai-msg-user' : ''}`}>
      {!isUser && (
        <div className="ai-msg-avatar">
          <i className="bx bxs-star" style={{ fontSize: 12, color: BRAND }} />
        </div>
      )}
      <div style={{ maxWidth: '78%' }}>
        {!isUser && msg.intent && intentMeta && (
          <span className="ai-intent-badge" style={{ color: intentMeta.color, background: intentMeta.bg }}>
            <i className={`bx ${intentMeta.icon}`} style={{ fontSize: 11 }} /> {intentMeta.label}
          </span>
        )}
        <div className={isUser ? 'ai-bubble-user' : 'ai-bubble-ai'}>
          {isUser ? (
            <span style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</span>
          ) : msg.loading && !msg.content ? (
            <TypingDots />
          ) : (
            <div className="ai-markdown">
              <ReactMarkdown
                components={{
                  a: ({ href, children }) => {
                    const fullHref = href?.startsWith('/api/ai/report/download/')
                      ? `${AI_BASE_URL}${href}` : href;
                    return <a href={fullHref} style={{ color: BRAND }} target="_blank" rel="noreferrer">{children}</a>;
                  },
                  p:    ({ children }) => <p style={{ margin: '0 0 6px' }}>{children}</p>,
                  ul:   ({ children }) => <ul style={{ paddingLeft: 18, margin: '0 0 6px' }}>{children}</ul>,
                  ol:   ({ children }) => <ol style={{ paddingLeft: 18, margin: '0 0 6px' }}>{children}</ol>,
                  li:   ({ children }) => <li style={{ marginBottom: 2 }}>{children}</li>,
                  code: ({ inline, children }) => inline
                    ? <code style={{ background: 'rgba(0,157,172,0.1)', color: BRAND_DARK, padding: '1px 5px', borderRadius: 4, fontSize: 12 }}>{children}</code>
                    : <pre style={{ background: '#1e293b', color: '#e2e8f0', padding: '10px 14px', borderRadius: 8, fontSize: 12, overflowX: 'auto', margin: '6px 0' }}><code>{children}</code></pre>,
                }}
              >
                {msg.content}
              </ReactMarkdown>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const EmptyState = ({ suggestions, onSuggestion }) => (
  <div className="ai-empty">
    <div className="ai-empty-glow">
      <div className="ai-empty-ring">
        <div className="ai-empty-avatar">
          <i className="bx bxs-star" style={{ fontSize: 26, color: '#fff' }} />
        </div>
      </div>
    </div>
    <p className="ai-empty-title">How can I help you?</p>
    <p className="ai-empty-sub">Ask about the system, query data, or generate reports.</p>
    <div className="ai-suggestions">
      {suggestions.map((s) => (
        <button key={s.text} className="ai-suggest-btn" onClick={() => onSuggestion(s.text)}>
          <i className={`bx ${s.icon} ai-suggest-icon`} />
          <span className="ai-suggest-text">{s.text}</span>
          <i className="bx bx-chevron-right ai-suggest-arrow" />
        </button>
      ))}
    </div>
  </div>
);

const CapPill = ({ color, icon, children }) => (
  <span className="ai-cap-pill" style={{ color, borderColor: color + '40', background: color + '15' }}>
    <i className={`bx ${icon}`} style={{ fontSize: 12 }} /> {children}
  </span>
);

const TypingDots = () => (
  <span style={{ display: 'flex', gap: 5, alignItems: 'center', padding: '2px 0' }}>
    {[0, 1, 2].map((i) => (
      <span key={i} className="ai-dot" style={{ animationDelay: `${i * 0.18}s` }} />
    ))}
  </span>
);

/* ── CSS ── */
const PANEL_CSS = `
  .ai-panel {
    position: fixed; right: 0; top: 0; bottom: 0;
    width: 420px; z-index: 1050;
    display: flex; flex-direction: column;
    background: #fff;
    border-left: 1px solid rgba(0,157,172,0.15);
    box-shadow: -8px 0 40px rgba(0,0,0,0.13);
    font-family: inherit;
    transition: transform 0.3s cubic-bezier(0.4,0,0.2,1), opacity 0.25s ease;
  }

  /* Header */
  .ai-header {
    background: linear-gradient(135deg, ${BRAND_DARK} 0%, ${BRAND} 55%, ${BRAND_LIGHT} 100%);
    padding: 13px 16px;
    display: flex; align-items: center; justify-content: space-between;
    flex-shrink: 0;
  }
  .ai-header-left  { display: flex; align-items: center; gap: 11px; }
  .ai-avatar-wrap  { position: relative; flex-shrink: 0; }
  .ai-avatar {
    width: 38px; height: 38px; border-radius: 50%;
    background: rgba(255,255,255,0.2);
    border: 2px solid rgba(255,255,255,0.35);
    display: flex; align-items: center; justify-content: center;
    backdrop-filter: blur(4px);
  }
  .ai-online-dot {
    position: absolute; bottom: 1px; right: 1px;
    width: 10px; height: 10px; border-radius: 50%;
    background: #4ade80; border: 2px solid ${BRAND};
  }
  .ai-header-title { font-weight: 700; font-size: 15px; color: #fff; letter-spacing: 0.1px; }
  .ai-header-sub   { font-size: 11px; color: rgba(255,255,255,0.78); margin-top: 2px; }
  .ai-icon-btn {
    background: rgba(255,255,255,0.18); border: none; color: #fff;
    border-radius: 7px; width: 30px; height: 30px;
    cursor: pointer; display: flex; align-items: center; justify-content: center;
    transition: background 0.15s;
  }
  .ai-icon-btn:hover { background: rgba(255,255,255,0.32); }

  /* Capability row */
  .ai-cap-row {
    display: flex; gap: 6px; padding: 9px 14px;
    border-bottom: 1px solid #f0f4f8; flex-wrap: wrap; flex-shrink: 0;
    background: #f8fbfc;
  }
  .ai-cap-pill {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 3px 10px; border-radius: 99px; font-size: 11px;
    font-weight: 600; border: 1px solid; letter-spacing: 0.2px;
  }

  /* Messages */
  .ai-messages {
    flex: 1; overflow-y: auto; padding: 16px 14px;
    display: flex; flex-direction: column; gap: 14px;
  }
  .ai-scroll::-webkit-scrollbar { width: 4px; }
  .ai-scroll::-webkit-scrollbar-track { background: transparent; }
  .ai-scroll::-webkit-scrollbar-thumb { background: #d1e8eb; border-radius: 99px; }
  .ai-scroll::-webkit-scrollbar-thumb:hover { background: #a8d5da; }

  /* Message bubbles */
  .ai-msg-row { display: flex; align-items: flex-end; gap: 8px; }
  .ai-msg-user { justify-content: flex-end; }
  .ai-msg-enter { animation: aiSlideIn 0.22s ease both; }
  @keyframes aiSlideIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }

  .ai-msg-avatar {
    width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0;
    background: rgba(0,157,172,0.1); border: 1px solid rgba(0,157,172,0.25);
    display: flex; align-items: center; justify-content: center;
    margin-bottom: 2px;
  }
  .ai-intent-badge {
    display: inline-flex; align-items: center; gap: 4px;
    font-size: 10px; font-weight: 700; letter-spacing: 0.4px;
    padding: 2px 8px; border-radius: 99px; margin-bottom: 5px;
  }
  .ai-bubble-user {
    background: linear-gradient(135deg, ${BRAND}, ${BRAND_DARK});
    color: #fff; padding: 10px 14px;
    border-radius: 16px 16px 4px 16px;
    font-size: 13px; line-height: 1.55;
    box-shadow: 0 2px 10px rgba(0,157,172,0.28);
  }
  .ai-bubble-ai {
    background: #f7fbfc; border: 1px solid #e2eef0;
    padding: 11px 14px; border-radius: 16px 16px 16px 4px;
    font-size: 13px; line-height: 1.65; color: #1e293b;
    box-shadow: 0 1px 4px rgba(0,0,0,0.05);
  }
  .ai-markdown p:last-child { margin-bottom: 0 !important; }

  /* Typing dots */
  @keyframes aiDotBounce { 0%,80%,100%{transform:translateY(0)} 40%{transform:translateY(-5px)} }
  .ai-dot {
    width: 7px; height: 7px; border-radius: 50%;
    background: ${BRAND_LIGHT}; display: inline-block;
    animation: aiDotBounce 1.3s infinite;
  }

  /* Input area */
  .ai-input-wrap { padding: 10px 12px; border-top: 1px solid #e8f4f5; background: #fff; flex-shrink: 0; }
  .ai-input-box {
    border: 1.5px solid #d0e8eb; border-radius: 12px;
    background: #f8fbfc; overflow: hidden;
    transition: border-color 0.2s, box-shadow 0.2s;
  }
  .ai-input-box:focus-within {
    border-color: ${BRAND} !important;
    box-shadow: 0 0 0 3px rgba(0,157,172,0.14) !important;
  }
  .ai-textarea {
    display: block; width: 100%; border: none; background: transparent;
    padding: 11px 13px 6px; font-size: 13px; outline: none; resize: none;
    font-family: inherit; line-height: 1.55; color: #1e293b;
    box-sizing: border-box; min-height: 42px;
  }
  .ai-textarea::placeholder { color: #94a3b8; }
  .ai-input-footer {
    display: flex; align-items: center; justify-content: space-between;
    padding: 5px 10px 8px;
  }
  .ai-input-hint { font-size: 11px; color: #94a3b8; }
  .ai-send-btn {
    width: 32px; height: 32px; border-radius: 9px;
    background: linear-gradient(135deg, ${BRAND}, ${BRAND_DARK});
    color: #fff; border: none; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0; transition: opacity 0.15s, transform 0.1s;
    box-shadow: 0 2px 8px rgba(0,157,172,0.3);
  }
  .ai-send-btn:hover { opacity: 0.88; transform: scale(1.05); }
  .ai-send-stop { background: linear-gradient(135deg, #ef4444, #dc2626) !important; box-shadow: 0 2px 8px rgba(220,38,38,0.3) !important; }
  .ai-send-disabled { background: #d1d5db !important; box-shadow: none !important; cursor: not-allowed !important; transform: none !important; }

  /* Empty state */
  .ai-empty {
    display: flex; flex-direction: column; align-items: center;
    padding: 28px 10px; text-align: center;
  }
  .ai-empty-glow {
    width: 84px; height: 84px; border-radius: 50%;
    background: radial-gradient(circle, rgba(0,157,172,0.18) 0%, transparent 70%);
    display: flex; align-items: center; justify-content: center; margin-bottom: 14px;
  }
  .ai-empty-ring {
    width: 62px; height: 62px; border-radius: 50%;
    border: 2px solid rgba(0,157,172,0.3);
    display: flex; align-items: center; justify-content: center;
  }
  .ai-empty-avatar {
    width: 48px; height: 48px; border-radius: 50%;
    background: linear-gradient(135deg, ${BRAND}, ${BRAND_DARK});
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 4px 16px rgba(0,157,172,0.4);
  }
  .ai-empty-title { font-size: 16px; font-weight: 700; color: #0f172a; margin: 0 0 5px; }
  .ai-empty-sub   { font-size: 12px; color: #64748b; margin: 0 0 20px; line-height: 1.55; max-width: 280px; }
  .ai-suggestions { display: flex; flex-direction: column; gap: 7px; width: 100%; text-align: left; }
  .ai-suggest-btn {
    display: flex; align-items: center; gap: 10px;
    background: #fff; border: 1px solid #e2eef0;
    border-radius: 10px; padding: 9px 13px; font-size: 12px;
    cursor: pointer; color: #334155; font-family: inherit;
    transition: border-color 0.15s, background 0.15s; width: 100%;
  }
  .ai-suggest-btn:hover { border-color: ${BRAND}; background: rgba(0,157,172,0.04); }
  .ai-suggest-btn:hover .ai-suggest-arrow { opacity: 1 !important; transform: translateX(3px) !important; }
  .ai-suggest-icon { font-size: 15px; flex-shrink: 0; color: ${BRAND}; }
  .ai-suggest-text { flex: 1; line-height: 1.4; }
  .ai-suggest-arrow { color: ${BRAND}; font-size: 16px; opacity: 0; transition: opacity 0.15s, transform 0.15s; flex-shrink: 0; }
`;

export default AIChatPanel;
